/**
 * H5 加载性能埋点（冷启整页 / SPA 路由切换）
 */

import { EPerformanceEvents } from '../events';
import { reportPerformanceEvent } from '../report';

import type { IColdNavigationPerfSnapshot } from './collect-navigation';

function appendOptionalDurations(payload: Record<string, unknown>, snapshot: IColdNavigationPerfSnapshot): void {
    const keys = [
        'dns_duration',
        'ssl_duration',
        'tcp_duration',
        'ttfb_duration',
        'response_duration',
        'dom_interactive_duration',
        'dcl_duration',
        'load_event_duration',
        'fp_duration',
        'fcp_duration',
    ] as const;

    keys.forEach(key => {
        const value = snapshot[key];
        if (value != null) {
            payload[key] = value;
        }
    });
}

export interface IH5ColdLoadPerfParams {
    entry_pathname: string;
    is_cold_start: number;
    /** 当前 app_version 下 entry_pathname 是否首次完成 H5 路由性能采样（与 is_cold_start 无关） */
    is_route_version_first_load: number;
    had_session_cookie: number;
    timing: IColdNavigationPerfSnapshot;
    /** Web 导航开始 → 首个内联 JS 执行的耗时（ms），无有效时间戳时不上报。 */
    navigation_to_js_duration: number | null;
    /** navigationStart → 双 rAF（ms）；fp/fcp 缺失时的绘制参考 */
    raf_paint_duration: number | null;
}

/**
 * 整页冷启加载性能（h5_cold_load_perf）
 *
 * @description 仅文档首次加载上报一次；含 DNS/SSL/TTFB/DCL/FP 等 Navigation/Paint Timing
 * TODO(perf-compat): fp/fcp 可能缺失；可参考 dcl_duration、raf_paint_duration
 */
export function logH5ColdLoadPerfTrack(params: IH5ColdLoadPerfParams): void {
    const {
        entry_pathname,
        is_cold_start,
        is_route_version_first_load,
        had_session_cookie,
        timing,
        navigation_to_js_duration,
        raf_paint_duration,
    } = params;

    const payload: Record<string, unknown> = {
        // 冷启首屏 pathname（文档内首次 pathname 同步时的路由）
        entry_pathname,
        // 当前 HTML 文档冷启入口，本事件固定 1
        is_cold_start,
        // 当前 app_version 下 entry_pathname 是否首次完成路由性能采样（与 is_cold_start 无关）
        is_route_version_first_load,
        // 冷启时刻是否已有 h5r_session_ready（影响 waitForSession 等待）
        had_session_cookie,
        load_type: 'cold',
    };
    // Navigation/Paint Timing：dns/ssl/ttfb/dcl/fp/fcp 等，有值才写入 payload
    appendOptionalDurations(payload, timing);
    if (navigation_to_js_duration != null) {
        payload.navigation_to_js_duration = navigation_to_js_duration;
    }
    if (raf_paint_duration != null) {
        // navigationStart → 双 rAF（ms）；fp/fcp 缺失时的绘制参考
        payload.raf_paint_duration = raf_paint_duration;
    }

    reportPerformanceEvent(EPerformanceEvents.H5_COLD_LOAD_PERF, payload);
}

export interface IH5SpaRoutePerfParams {
    to_pathname: string;
    from_pathname: string;
    route_to_paint_duration: number;
    is_cold_start: number;
    /** 当前 app_version 下 to_pathname 是否首次完成 H5 路由性能采样 */
    is_route_version_first_load: number;
}

/**
 * SPA 路由切换性能（h5_spa_route_perf）
 *
 * @description pathname 变更 → 双 rAF 近似首帧；无 DNS/SSL（浏览器不为客户端路由新建 navigation）
 */
export function logH5SpaRoutePerfTrack(params: IH5SpaRoutePerfParams): void {
    reportPerformanceEvent(EPerformanceEvents.H5_SPA_ROUTE_PERF, {
        to_pathname: params.to_pathname,
        from_pathname: params.from_pathname,
        route_to_paint_duration: params.route_to_paint_duration,
        is_cold_start: params.is_cold_start,
        is_route_version_first_load: params.is_route_version_first_load,
        load_type: 'spa',
    });
}
